import React, {useCallback, useEffect} from 'react';
import {metrics} from '~/vendor/metrics';
import {useNavigate} from 'react-router-dom';
import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';
import {
  Button,
  Typography,
  LightBulbIcon,
  Link,
  CheckIcon,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  ArrowUpIcon,
  useMobileBreakpoint
} from '@customink/pigment-react';
import {Octopus1} from './Octopus1';
import embroideredOctopus from '~/assets/images/embroidery/Octopus2.png';

const EMBROIDERY_TIPS = [
  {
    heading: 'Keep your design simple',
    subtitle: "Small details aren't captured well."
  },
  {
    heading: 'Choose a bold font',
    subtitle: 'Thin lines in text will be hard to read.'
  },
  {
    heading: 'Use solid colors',
    subtitle: 'Gradients cannot be embroidered.'
  }
];

interface EmbroideryModalProps {
  open: boolean;
  onClose: () => void;
}

const EmbroideryModal: React.FC<EmbroideryModalProps> = ({open, onClose}) => {
  const navigate = useNavigate();
  const isMobile = useMobileBreakpoint();

  useEffect(() => {
    metrics.pageViewTag('/ndx/designtip/embroidery');
  }, []);

  const handleCloseClicked = useCallback(() => {
    onClose();
    metrics.pageViewTag('/ndx/designtip/embroidery/closed');
  }, [open, onClose, metrics]);

  const handleDoneClicked = useCallback(() => {
    metrics.pageViewTag('/ndx/designtip/embroidery/confirm');
    onClose();
  }, [open, onClose, metrics]);

  const handleLinkClicked = useCallback(() => {
    onClose();
    metrics.pageViewTag('/ndx/designtip/embroidery/help');
    navigate('/help');
  }, [open, onClose, metrics]);
  return (
    <Dialog
      open={open}
      title="Design Tips for Embroidery"
      onClose={handleCloseClicked}
      width="lg">
      <DialogContent dividers={false}>
        <Typography variant={isMobile ? 'paragraphSmall' : 'paragraphDefault'}>
          Embroidery is stitched directly into the product. For the best
          results, stick to these tips or{' '}
          <Link onClick={handleLinkClicked}>reach out for help.</Link>
        </Typography>
        <Stack
          sx={{pt: {sm: 2, md: 1}}}
          spacing={3}
          direction={isMobile ? 'column-reverse' : 'row'}>
          <Stack spacing={2} justifyContent="center">
            {EMBROIDERY_TIPS.map((tip, index) => (
              <Stack key={index} direction="row" spacing={1}>
                <CheckIcon color="success" />
                <Stack spacing={0.5}>
                  <Typography variant={isMobile ? 'heading6' : 'heading5'}>
                    {tip.heading}
                  </Typography>
                  <Typography
                    variant={isMobile ? 'uiTextSmall' : 'paragraphDefault'}>
                    {tip.subtitle}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Stack
            padding={isMobile ? 2 : 3}
            spacing={3}
            sx={{
              borderRadius: '8px',
              border: `1px solid ${designTokens.color.gray.accent.value}`
            }}>
            <Stack spacing={1} direction="row" justifyItems="center">
              <LightBulbIcon />
              <Typography
                variant={isMobile ? 'paragraphTiny' : 'paragraphSmall'}>
                Our artists will also adjust your image for the best results.
              </Typography>
            </Stack>
            <Stack
              spacing={1.3}
              direction="row"
              justifyItems="center"
              alignItems="center"
              display="grid"
              sx={{
                gridTemplateColumns: '1fr 44px 1fr',
                maxWidth: isMobile ? '100%' : '450px'
              }}>
              <Octopus1 />
              <ArrowUpIcon sx={{transform: 'rotate(90deg)'}} />
              <img
                style={{aspectRatio: '1 / 1', width: '100%'}}
                src={embroideredOctopus}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="large" onClick={handleDoneClicked} fullWidth={isMobile}>
          Got It!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmbroideryModal;
